import { Box, Divider, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { FunctionComponent, useEffect, useRef } from 'react';
import { EXPERIMENTS, LOCALES, SearchResultsFilter } from '../../../../constants';
import { DATA_HOOKS } from '../../../../dataHooks';
import {
  ArticleSearchResultItem,
  ArticleSourceType,
  ExternalResourceType,
  PageType,
  SearchResultItem,
} from '../../../../types';
import { InnerLink } from '../../../InnerLink';
import css from '../index.module.scss';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { SearchResultDropdownItem } from './SearchResultDropDownItem';
import { ArticleType } from '@wix/answers-api';
import { DeprecatedInternalSearchBarResults } from '../DeprecatedInternalSearchBarResults';
import { useOnVisible } from '../../../../hooks/useOnVisible';
import { useSearchBarBI } from '../../bi';
import { useRouter } from 'next/router';


export type ResultsResourceGroupProps = {
  searchResults: ArticleSearchResultItem[] | SearchResultItem[];
  inputText: string;
  focusedArticleIndex: number;
  onSearchResultClick: (
    url: string,
    type: ArticleSourceType,
    index: number,
    id?: string
  ) => Promise<void>;
  setFocusedArticleIndex: (index: number) => void;
  onShowMoreClick?: (resourceType: ArticleSourceType) => Promise<void>;
  prefixSize: number;
  isShowMoreVisible?: boolean;
  setFocusedResult?: (result: SearchResultItem) => void;
  pageType: PageType;
  searchSessionId: string;
};

export const mapArticleTypeToFilter = {
  [ArticleType.ARTICLE]: SearchResultsFilter.ARTICLES,
  [ArticleType.KNOWN_ISSUE]: SearchResultsFilter.KNOWN_ISSUES,
  [ArticleType.FEATURE_REQUEST]: SearchResultsFilter.FEATURE_REQUESTS,
  [ArticleType.VIDEO]: SearchResultsFilter.ARTICLES,
  [ExternalResourceType.WIX_SEO_LEARN]: ExternalResourceType.WIX_SEO_LEARN,
  [ExternalResourceType.WIX_LEARN]: ExternalResourceType.WIX_LEARN,
  [ExternalResourceType.WIX_BLOG]: ExternalResourceType.WIX_BLOG,
} as const;

export const ResultsResourceGroup: FunctionComponent<
  ResultsResourceGroupProps
> = ({
  searchResults,
  inputText,
  focusedArticleIndex,
  onSearchResultClick,
  setFocusedArticleIndex,
  onShowMoreClick,
  prefixSize,
  isShowMoreVisible,
  setFocusedResult,
  pageType,
  searchSessionId
}) => {
  const { t } = useTranslation();
  const { experiments } = useExperiments({ suspense: true, readOnly: true });
  const { locale } = useRouter();
  const resultsGroupRef = useRef<HTMLDivElement>(null);
  const isVisible = useOnVisible(resultsGroupRef);
  const { viewResultsGroupBI } = useSearchBarBI();


  useEffect(() => {
    if (isVisible && searchResults.length === 1) {
      void viewResultsGroupBI(searchResults[0].type, pageType, searchSessionId);
    }
  }, [isVisible, searchResults]);

  const onSecondItemVisible = async () => {
    await viewResultsGroupBI(searchResults[0].type, pageType, searchSessionId);
  };

  const groupType = searchResults[0].type;
  const isExternalSearchResultsEnabled = experiments.enabled(
    EXPERIMENTS.SPECS.EXTERNAL_SEARCH
  ) && locale === LOCALES.EN;

  const onFocusedIndexCheck = (index: number) => {
    const isFocused = prefixSize + index === focusedArticleIndex;
    if (isFocused && setFocusedResult) {
      setFocusedResult(searchResults[index]);
    }
    return isFocused;
  };

  const getFilterType = (type: ArticleType | ExternalResourceType) =>
    mapArticleTypeToFilter[type];
  return (
    <>
      {isExternalSearchResultsEnabled ? (
        <Box
          className={css.searchBarResultsList}
          direction="vertical"
          dataHook={DATA_HOOKS.SEARCH_BAR_RESULT_GROUP}
          ref={resultsGroupRef}
        >
          <Box
            direction="vertical"
            dataHook={`${DATA_HOOKS.SEARCH_BAR_RESULTS}_${groupType}`}
          >
            <Box
              align="space-between"
              className={css.resultsGroupTitle}
              verticalAlign="middle"
              paddingRight="SP4"
              paddingLeft="SP4"
              height="36px"
            >
              <Text
                size="small"
                weight="normal"
                color={'D30'}
                className={css.searchBarResultTypeTitle}
                dataHook={DATA_HOOKS.SEARCH_BAR_RESULT_TYPE_TITLE}
              >
                {t(`search-bar-results.type.group.${searchResults[0].type}`)}
              </Text>
              {isShowMoreVisible ? (
                <InnerLink
                  href={`/search?term=${inputText}&filter=${getFilterType(
                    searchResults[0].type
                  )}`}
                  className={css.seeMoreResults}
                  dataHook={`${DATA_HOOKS.SEARCH_BAR_RESULT_TYPE_MORE}_${groupType}`}
                  onMouseEnter={() => setFocusedArticleIndex(-1)}
                  onClick={async () => {
                    if (onShowMoreClick) {
                      await onShowMoreClick(groupType);
                    }
                  }}
                >
                  <Text
                    size="small"
                    weight="thin"
                    className={css.seeMoreResultsText}
                  >
                    {t('searchbar.show-more')}
                  </Text>
                </InnerLink>
              ) : null}
            </Box>
            <Box
              className={css.searchBarResultsList}
              align="left"
              direction="vertical"
            >
              <Divider />

              {searchResults.map((searchResult, index) => (
                <SearchResultDropdownItem
                  searchResult={searchResult}
                  groupIndex={index}
                  listIndex={prefixSize + index}
                  key={index}
                  onSearchResultClick={onSearchResultClick}
                  isFocused={onFocusedIndexCheck(index)}
                  itemScrolledIntoView={onSecondItemVisible}
                />
              ))}
            </Box>
            <Divider />
            <div aria-live="assertive" key={inputText} className={css.srOnly}>
              <>
                {t(`searchbar.results.reader`, {
                  count: searchResults.length,
                })}
              </>
            </div>
          </Box>
        </Box>
      ) : (
        <DeprecatedInternalSearchBarResults
          articles={searchResults as ArticleSearchResultItem[]}
          inputText={inputText}
          focusedArticleIndex={focusedArticleIndex as number}
          onSearchResultClick={onSearchResultClick}
          setFocusedArticleIndex={setFocusedArticleIndex}
        />
      )}
    </>
  );
};
